/**
 * Custom hook for managing account balance calculations
 * Handles hybrid balance logic and memoization
 */
import { useMemo, useRef } from 'react';
import { calculateAccountBalanceFromPositions, AccountWithPositions } from '../components/ui/container-utils';
import { groupAccountsByInstitution, InstitutionGroup as BaseInstitutionGroup } from '../utils/display-utils';
import type { WealthicaAccount } from '../types';

interface UseAccountBalancesOptions {
  wealthicaAccounts: WealthicaAccount[];
  accountsWithPositions: AccountWithPositions[];
  loadingPositionsFor: Record<string, boolean>;
}

interface InstitutionGroup extends BaseInstitutionGroup {
  accounts: Array<
    WealthicaAccount & {
      calculatedBalance: number;
      holdingsTotal: number;
      accountTotal: number;
    }
  >;
}

export function useAccountBalances({
  wealthicaAccounts,
  accountsWithPositions,
  loadingPositionsFor
}: UseAccountBalancesOptions) {
  // Circuit breaker refs to avoid runaway recomputations under fast-changing inputs
  const prevGroupsRef = useRef<InstitutionGroup[] | null>(null);
  const lastKeysRef = useRef<{ w: string; p: string; l: string }>({ w: '', p: '', l: '' });
  const churnRef = useRef<{ count: number; windowStart: number }>({ count: 0, windowStart: 0 });

  // Build lightweight, stable dependency keys to avoid re-computation
  // when input arrays are recreated with identical content across renders.
  const wealthicaAccountsKey = (wealthicaAccounts || [])
    .map(a => `${a.id}:${a.balance ?? 0}`)
    .join('|');
  const accountsWithPositionsKey = (accountsWithPositions || [])
    .map(a => `${a.id}:${a.positions?.length || 0}`)
    .join('|');
  const loadingPositionsKey = (() => {
    const keys = Object.keys(loadingPositionsFor || {}).sort();
    const active = keys.filter(k => !!(loadingPositionsFor as Record<string, boolean>)[k]).length;
    return `${keys.join(',')}:${active}`;
  })();
  // Memoized institution groups with hybrid balance calculations
  const institutionGroups = useMemo((): InstitutionGroup[] => {
    // Lightweight circuit breaker: if keys are toggling too rapidly within a short window,
    // return the last stable result to prevent render storms. This is defensive and only
    // activates under pathological churn.
    const now = Date.now();
    const WINDOW_MS = 1500;
    const MAX_CHANGES = 8;
    const keysChanged = (
      lastKeysRef.current.w !== wealthicaAccountsKey ||
      lastKeysRef.current.p !== accountsWithPositionsKey ||
      lastKeysRef.current.l !== loadingPositionsKey
    );
    if (keysChanged) {
      if (now - churnRef.current.windowStart > WINDOW_MS) {
        churnRef.current.windowStart = now;
        churnRef.current.count = 1;
      } else {
        churnRef.current.count += 1;
      }
      if (churnRef.current.count > MAX_CHANGES && prevGroupsRef.current) {
        // Short-circuit with last stable value
        return prevGroupsRef.current;
      }
    }

    const groups = groupAccountsByInstitution(wealthicaAccounts || []);
    
    return groups.map(institution => ({
      ...institution,
      accounts: institution.accounts.map(account => {
        const accountWithPositions = accountsWithPositions.find(a => a.id === account.id);
        const hasPositions = accountWithPositions?.positions?.length > 0;
        const isNotLoading = !loadingPositionsFor[account.id];
        const positionsLoaded = hasPositions && isNotLoading;

        // Sum positions' market_value for this account (0 when none)
        const holdingsTotal = hasPositions
          ? accountWithPositions!.positions.reduce((sum: number, p: any) => sum + (p?.market_value || 0), 0)
          : 0;

        // Keep existing calculatedBalance semantics for backwards compatibility
        const calculatedBalance = hasPositions
          ? calculateAccountBalanceFromPositions(account.id, wealthicaAccounts || [], accountsWithPositions)
          : account.balance || 0;

        const accountBalance = account.balance || 0;
        const accountTotal = holdingsTotal + accountBalance;

        return {
          ...account,
          calculatedBalance,
          holdingsTotal,
          accountTotal,
          _debug: {
            hasPositions,
            isNotLoading,
            positionsLoaded,
            originalBalance: account.balance,
            usingCalculatedBalance: hasPositions
          }
        };
      }),
      // Institution total uses API balances only (matches existing behavior and page totals)
      totalBalance: institution.accounts.reduce((sum, account) => {
        return sum + (account.balance || 0);
      }, 0)
    }));
  // We intentionally depend on stable keys rather than array/object identities
  // to prevent recalculation on each render when references change but content has not.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wealthicaAccountsKey, accountsWithPositionsKey, loadingPositionsKey]);

  // Update last-known stable result and keys after memo computation
  // This runs on every render; the refs are used only for circuit breaking above
  lastKeysRef.current = { w: wealthicaAccountsKey, p: accountsWithPositionsKey, l: loadingPositionsKey };
  if (institutionGroups) {
    prevGroupsRef.current = institutionGroups;
  }

  return {
    institutionGroups
  };
}